import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { getPositionsNearStopLoss, calcAccountRiskPercent, filterOpenTradesByWidgetSettings, formatFilterCriteria, HIGH_RISK_EXPOSURE_THRESHOLD, type WidgetFilterSettings } from '../lib/tradeCalculations'
import { supabase } from '../lib/supabase'
import { navigateToTradeDetail } from '../utils/tradeNavigation'
import { handleScrollGuard, scrollContainerProps } from '../utils/scrollGuard'
import DashboardWidgetTitle from './DashboardWidgetTitle'
import InboxStudioModal from './InboxStudioModal'
import TradeListQuickActions, { type TradeQuickModalKind } from './TradeListQuickActions'
import { addInboxDismissedKey, readInboxDismissedKeys } from '../lib/inboxDismissed'
import {
  buildInboxStudioMessage,
  feedItemKey,
  type InboxFeedItem,
  type InboxOpenTrade,
} from '../lib/inboxStudioMessages'
import type { Trade } from '../types/trade'

interface NotificationRow {
  id: string
  user_id: string
  title: string
  body: string | null
  type: string
  read_at: string | null
  created_at: string
  metadata?: Record<string, unknown>
}

interface OpenTrade {
  id: string
  symbol: string
  long_short: 'long' | 'short'
  entry_date: string | null
  entry_price: number
  exit_date?: string | null
  stop_loss: number | null
  take_profit: number | null
  current_price?: number | null
  strategy: string | null
  strategy_id: string | null
  [key: string]: unknown
}

interface FeedAccount {
  id: string
  currency: string
  initial_balance?: number | null
}

interface TotalRiskExposureWidgetConfig {
  id: string
  filterSettings: WidgetFilterSettings
}

interface DashboardFeedProps {
  openTrades?: OpenTrade[]
  closedTradesLast2Weeks?: OpenTrade[]
  refreshTrigger?: number
  trades?: OpenTrade[]
  accounts?: FeedAccount[]
  selectedCurrency?: string
  latestRates?: Map<string, number>
  totalRiskExposureWidgets?: TotalRiskExposureWidgetConfig[]
  onTradeUpdated?: () => void
  presentation?: 'widget' | 'page' | 'count' | 'studio'
  onCountChange?: (count: number) => void
  /** Studio inbox (/app-test): open trade quick review */
  onStudioOpenTrade?: (trade: OpenTrade) => void
  onStudioOpenTradeReview?: (trade: OpenTrade) => void
  onStudioEditTrade?: (trade: OpenTrade) => void
  detailBasePath?: 'app' | 'app-test'
}

const cardClass =
  'dashboard-card p-2 md:p-3'

function FeedActionLink({
  label,
  onClick,
  tone = 'orange',
}: {
  label: string
  onClick: (e: MouseEvent<HTMLButtonElement>) => void
  tone?: 'orange' | 'red' | 'green' | 'blue' | 'purple' | 'amber' | 'gray'
}) {
  const toneClass =
    tone === 'red'
      ? 'text-red-600 hover:text-red-700'
      : tone === 'green'
        ? 'text-green-600 hover:text-green-700'
        : tone === 'blue'
          ? 'text-blue-600 hover:text-blue-700'
          : tone === 'purple'
            ? 'text-purple-600 hover:text-purple-700'
            : tone === 'amber'
              ? 'text-amber-600 hover:text-amber-700'
              : tone === 'gray'
                ? 'text-gray-600 hover:text-gray-700'
                : 'text-orange-600 hover:text-orange-700'

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-shrink-0 self-start text-xs font-medium hover:underline whitespace-nowrap ${toneClass}`}
    >
      {label}
    </button>
  )
}

function FeedItemRow({
  className = '',
  onClick,
  icon,
  children,
  action,
}: {
  className?: string
  onClick?: () => void
  icon: ReactNode
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
      className={`flex gap-2 p-1.5 rounded-lg border ${
        onClick ? 'cursor-pointer hover:opacity-95 transition-opacity' : ''
      } ${className}`}
    >
      {icon}
      <div className="flex-1 min-w-0">{children}</div>
      {action}
    </div>
  )
}

function iconForType(type: string) {
  switch (type) {
    case 'missing_data':
      return (
        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </span>
      )
    case 'near_sl':
      return (
        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </span>
      )
    case 'high_risk_exposure':
      return (
        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </span>
      )
    case 'alert':
    case 'stop_loss':
      return (
        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </span>
      )
    case 'add_first_strategy':
      return (
        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </span>
      )
    case 'after_exit_review':
      return (
        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        </span>
      )
    case 'take_profit':
      return (
        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </span>
      )
    case 'trade':
      return (
        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8v-3a3 3 0 00-3-3h-5" />
          </svg>
        </span>
      )
    case 'system':
      return (
        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          </svg>
        </span>
      )
    default:
      return (
        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </span>
      )
  }
}

type OpenTradeWithDistance = OpenTrade & { distance?: number }

type FeedItem =
  | { kind: 'notification'; data: NotificationRow }
  | { kind: 'missing_data'; data: OpenTrade }
  | { kind: 'sl_reached'; data: OpenTrade }
  | { kind: 'tp_reached'; data: OpenTrade }
  | { kind: 'near_sl'; data: OpenTradeWithDistance }
  | { kind: 'after_exit_review'; data: OpenTrade }
  | { kind: 'add_first_strategy' }
  | { kind: 'high_risk_exposure'; data: { widgetId: string; riskPercent: number; criteria?: string } }

function isMissingImportantData(trade: OpenTrade): boolean {
  const { missingSL, missingStrategy } = getMissingFields(trade)
  return missingSL || missingStrategy
}

function getMissingFields(trade: OpenTrade): { missingSL: boolean; missingStrategy: boolean } {
  const missingSL = trade.stop_loss == null || trade.stop_loss === 0
  const missingStrategy =
    (!trade.strategy_id || trade.strategy_id === '') &&
    (!trade.strategy || String(trade.strategy).trim() === '')
  return { missingSL, missingStrategy }
}

function getMissingDataBodyKey(trade: OpenTrade): string {
  const { missingSL, missingStrategy } = getMissingFields(trade)
  if (missingSL && missingStrategy) return 'trading:dashboard.feedMissingDataBodyBoth'
  if (missingSL) return 'trading:dashboard.feedMissingDataBodySL'
  if (missingStrategy) return 'trading:dashboard.feedMissingDataBodyStrategy'
  return 'trading:dashboard.feedMissingDataBodySL' // fallback (should not happen)
}

function isStopLossReached(trade: OpenTrade): boolean {
  const price = trade.current_price
  const sl = trade.stop_loss
  if (price == null || sl == null || sl === 0) return false
  if (trade.long_short === 'long') return price < sl
  return price > sl
}

function isTakeProfitReached(trade: OpenTrade): boolean {
  const price = trade.current_price
  const tp = trade.take_profit
  if (price == null || tp == null || tp === 0) return false
  if (trade.long_short === 'long') return price >= tp
  return price <= tp
}

export default function DashboardFeed({
  openTrades = [],
  closedTradesLast2Weeks = [],
  refreshTrigger,
  trades = [],
  accounts = [],
  selectedCurrency = 'USD',
  latestRates = new Map(),
  totalRiskExposureWidgets = [],
  onTradeUpdated,
  presentation = 'widget',
  onCountChange,
  onStudioOpenTrade,
  onStudioOpenTradeReview,
  onStudioEditTrade,
  detailBasePath = 'app',
}: DashboardFeedProps) {
  const { t } = useTranslation(['trading', 'common'])
  const { user } = useAuth()
  const { membershipType } = useMembership()
  const navigate = useNavigate()

  const handleNavigateToDetail = (trade: OpenTrade) => {
    if (presentation === 'studio' && onStudioOpenTrade) {
      onStudioOpenTrade(trade)
      return
    }
    navigateToTradeDetail(trade, navigate, membershipType, undefined, detailBasePath)
  }

  const handleNavigateToDetailWithReview = (trade: OpenTrade) => {
    if (presentation === 'studio' && onStudioOpenTradeReview) {
      onStudioOpenTradeReview(trade)
      return
    }
    navigateToTradeDetail(trade, navigate, membershipType, { openAddEventWithType: 'review' }, detailBasePath)
  }

  const [items, setItems] = useState<NotificationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [tradeIdsWithReview, setTradeIdsWithReview] = useState<Set<string>>(new Set())
  const [hasStrategies, setHasStrategies] = useState<boolean | null>(null)
  const [feedQuickAction, setFeedQuickAction] = useState<{
    trade: OpenTrade
    kind: TradeQuickModalKind
  } | null>(null)
  const [studioSelectedKey, setStudioSelectedKey] = useState<string | null>(null)
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(() => new Set())

  const resolveTradeForQuickAction = (trade: OpenTrade): Trade =>
    (trades.find((t) => t.id === trade.id) ?? trade) as unknown as Trade

  const openFeedQuickAction = (trade: OpenTrade, kind: TradeQuickModalKind) => (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setFeedQuickAction({ trade, kind })
  }

  const handleQuickActionUpdated = () => {
    setFeedQuickAction(null)
    onTradeUpdated?.()
  }

  useEffect(() => {
    if (!user?.id) {
      setDismissedKeys(new Set())
      return
    }
    setDismissedKeys(readInboxDismissedKeys(user.id))
  }, [user?.id])

  const closedTradeIds = useMemo(
    () => closedTradesLast2Weeks.map((t) => t.id),
    [closedTradesLast2Weeks]
  )

  useEffect(() => {
    const fetchReviewTradeIds = async () => {
      if (!user || closedTradeIds.length === 0) {
        setTradeIdsWithReview(new Set())
        return
      }
      try {
        const { data, error } = await supabase
          .from('trade_history')
          .select('trade_id')
          .eq('event_type', 'review')
          .in('trade_id', closedTradeIds)
        if (error) throw error
        setTradeIdsWithReview(new Set((data || []).map((r) => (r as { trade_id: string }).trade_id)))
      } catch (e) {
        console.error('Failed to fetch review status:', e)
        setTradeIdsWithReview(new Set())
      }
    }
    fetchReviewTradeIds()
  }, [user, closedTradeIds, refreshTrigger])

  const tradesNeedingReview = useMemo(
    () =>
      closedTradesLast2Weeks.filter((t) => !tradeIdsWithReview.has(t.id)),
    [closedTradesLast2Weeks, tradeIdsWithReview]
  )

  const missingDataTrades = useMemo(
    () => (openTrades as OpenTrade[]).filter(isMissingImportantData),
    [openTrades]
  )

  const slReachedTrades = useMemo(
    () => (openTrades as OpenTrade[]).filter(isStopLossReached),
    [openTrades]
  )

  const tpReachedTrades = useMemo(
    () => (openTrades as OpenTrade[]).filter(isTakeProfitReached),
    [openTrades]
  )

  const nearSlTrades = useMemo(() => {
    const slReachedIds = new Set(slReachedTrades.map((t) => t.id))
    const near = getPositionsNearStopLoss(openTrades as OpenTrade[], 3)
    return near.filter((t) => !slReachedIds.has(t.id))
  }, [openTrades, slReachedTrades])

  const highRiskExposureItems = useMemo(() => {
    if (totalRiskExposureWidgets.length === 0) return []

    const openTrades = trades.filter((t) => !t.exit_date)
    const tradeRows = trades as Parameters<typeof calcAccountRiskPercent>[0]

    return totalRiskExposureWidgets.flatMap((widget) => {
      const filteredOpenTrades = filterOpenTradesByWidgetSettings(openTrades, widget.filterSettings)
      const riskPercent = calcAccountRiskPercent(tradeRows, accounts, selectedCurrency, latestRates, {
        accountId: widget.filterSettings.accountId,
        openTrades: filteredOpenTrades as Parameters<typeof calcAccountRiskPercent>[0],
      })
      if (riskPercent == null || riskPercent <= HIGH_RISK_EXPOSURE_THRESHOLD) return []

      const criteria = formatFilterCriteria(widget.filterSettings, [], accounts)
      return [
        {
          widgetId: widget.id,
          riskPercent,
          criteria: criteria || undefined,
        },
      ]
    })
  }, [totalRiskExposureWidgets, trades, accounts, selectedCurrency, latestRates])

  useEffect(() => {
    const fetchStrategiesCount = async () => {
      if (!user) {
        setHasStrategies(null)
        return
      }
      try {
        const { count, error } = await supabase
          .from('strategies')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
        if (error) throw error
        setHasStrategies((count ?? 0) > 0)
      } catch (e) {
        console.error('Failed to fetch strategies count:', e)
        setHasStrategies(null)
      }
    }
    fetchStrategiesCount()
  }, [user, refreshTrigger])

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) {
        setItems([])
        setLoading(false)
        return
      }
      try {
        const { data, error } = await supabase
          .from('notifications')
          .select('id, user_id, title, body, type, read_at, created_at, metadata')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(50)
        if (error) throw error
        setItems((data as NotificationRow[]) || [])
      } catch (e) {
        console.error('Failed to fetch feed:', e)
        setItems([])
      } finally {
        setLoading(false)
      }
    }
    fetchNotifications()
  }, [user, refreshTrigger])

  const feedItems = useMemo((): FeedItem[] => {
    const notifs = items
      .filter(
        (n) =>
          n.type !== 'stop_loss' && n.type !== 'take_profit' // Derive SL/TP from open trades, don't show DB notifications
      )
      .map((n) => ({ kind: 'notification' as const, data: n }))
    const missing: FeedItem[] = missingDataTrades.map((t) => ({ kind: 'missing_data' as const, data: t }))
    const slReached: FeedItem[] = slReachedTrades.map((t) => ({ kind: 'sl_reached' as const, data: t }))
    const tpReached: FeedItem[] = tpReachedTrades.map((t) => ({ kind: 'tp_reached' as const, data: t }))
    const nearSl: FeedItem[] = nearSlTrades.map((t) => ({ kind: 'near_sl' as const, data: t }))
    const afterReview: FeedItem[] = tradesNeedingReview.map((t) => ({
      kind: 'after_exit_review' as const,
      data: t
    }))
    const addFirstStrategy: FeedItem[] =
      hasStrategies === false ? [{ kind: 'add_first_strategy' as const }] : []
    const highRiskExposure: FeedItem[] = highRiskExposureItems.map((item) => ({
      kind: 'high_risk_exposure' as const,
      data: item,
    }))
    return [...slReached, ...tpReached, ...nearSl, ...highRiskExposure, ...afterReview, ...addFirstStrategy, ...missing, ...notifs]
  }, [items, missingDataTrades, slReachedTrades, tpReachedTrades, nearSlTrades, tradesNeedingReview, hasStrategies, highRiskExposureItems])

  const inboxVisibleFeedItems = useMemo((): FeedItem[] => {
    if (presentation !== 'studio' && presentation !== 'count') return feedItems
    return feedItems.filter((item) => !dismissedKeys.has(feedItemKey(item as InboxFeedItem)))
  }, [feedItems, dismissedKeys, presentation])

  useEffect(() => {
    const count =
      presentation === 'studio' || presentation === 'count' ? inboxVisibleFeedItems.length : feedItems.length
    onCountChange?.(count)
  }, [feedItems.length, inboxVisibleFeedItems.length, onCountChange, presentation])

  const handleEditTrade = (trade: OpenTrade) => {
    if (presentation === 'studio' && onStudioEditTrade) {
      onStudioEditTrade(trade)
      return
    }
    navigate(`/app/edit-trade/${trade.id}`)
  }

  const resolveOpenTrade = (trade: InboxOpenTrade | OpenTrade) =>
    (trades.find((entry) => entry.id === trade.id) ?? trade) as OpenTrade

  const markNotificationRead = async (notificationId: string) => {
    if (!user) return
    try {
      await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', user.id)
      setItems((current) =>
        current.map((row) =>
          row.id === notificationId ? { ...row, read_at: new Date().toISOString() } : row
        )
      )
    } catch (error) {
      console.error('Failed to mark notification read:', error)
    }
  }

  const dismissFeedItem = (item: FeedItem) => {
    if (!user?.id) return
    const key = feedItemKey(item as InboxFeedItem)
    setDismissedKeys((current) => addInboxDismissedKey(user.id, key, current))
    if (studioSelectedKey === key) setStudioSelectedKey(null)
    if (item.kind === 'notification' && !item.data.read_at) {
      void markNotificationRead(item.data.id)
    }
  }

  const studioMessages = useMemo(() => {
    if (presentation !== 'studio') return []
    return inboxVisibleFeedItems.map((item) =>
      buildInboxStudioMessage(item as InboxFeedItem, t, navigate, {
        openTrade: (trade) => handleNavigateToDetail(resolveOpenTrade(trade)),
        openTradeById: (tradeId) => {
          const trade = trades.find((entry) => entry.id === tradeId)
          if (trade) handleNavigateToDetail(trade as OpenTrade)
        },
        openTradeReview: (trade) => handleNavigateToDetailWithReview(resolveOpenTrade(trade)),
        editTrade: (trade) => handleEditTrade(resolveOpenTrade(trade)),
        quickAction: (trade, kind) => setFeedQuickAction({ trade: resolveOpenTrade(trade), kind }),
      })
    )
  }, [presentation, inboxVisibleFeedItems, t, navigate, trades])

  const studioSelectedMessage = useMemo(
    () => studioMessages.find((message) => message.key === studioSelectedKey) ?? null,
    [studioMessages, studioSelectedKey]
  )

  const openStudioMessage = (item: FeedItem) => {
    const key = feedItemKey(item as InboxFeedItem)
    setStudioSelectedKey(key)
    if (item.kind === 'notification' && !item.data.read_at) {
      void markNotificationRead(item.data.id)
    }
  }

  const [mobileExpanded, setMobileExpanded] = useState(false)

  if (!user) return null

  /** Renders feed list content - keyPrefix ensures unique keys when same list is rendered in both mobile and desktop (avoids React removeChild DOM mismatch) */
  const renderFeedListContent = (keyPrefix: 'desktop' | 'mobile') =>
    loading ? (
      <div className="py-6 text-center text-sm text-gray-500">
        {t('common:header.loading')}
      </div>
    ) : feedItems.length === 0 ? (
      <div className="py-6 text-center text-sm text-gray-500">
        {t('trading:dashboard.feedEmpty')}
      </div>
    ) : (
          <ul className="space-y-2">
            {feedItems.map((item) => {
              if (item.kind === 'notification') {
                const tradeId =
                  typeof item.data.metadata?.trade_id === 'string' ? item.data.metadata.trade_id : null
                const linkedTrade = tradeId ? trades.find((t) => t.id === tradeId) : undefined
                return (
                  <li
                    key={`${keyPrefix}-notif-${item.data.id}`}
                    className={item.data.read_at ? undefined : 'rounded-lg'}
                  >
                    <FeedItemRow
                      className={`gap-3 p-2 ${
                        item.data.read_at ? 'border-transparent bg-transparent' : 'border-orange-200 bg-orange-50/50'
                      }`}
                      onClick={linkedTrade ? () => handleNavigateToDetail(linkedTrade) : undefined}
                      icon={iconForType(item.data.type)}
                      action={
                        linkedTrade ? (
                          <FeedActionLink
                            label={t('trading:dashboard.feedActionViewTrade')}
                            tone="gray"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleNavigateToDetail(linkedTrade)
                            }}
                          />
                        ) : undefined
                      }
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">{item.data.title}</p>
                      {item.data.body && (
                        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{item.data.body}</p>
                      )}
                      <p className="text-[10px] text-gray-400 mt-1">
                        {new Date(item.data.created_at).toLocaleDateString(undefined, {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </FeedItemRow>
                  </li>
                )
              }
              if (item.kind === 'near_sl') {
                const nearItem = item as { kind: 'near_sl'; data: OpenTradeWithDistance }
                const distance = nearItem.data.distance ?? 0
                return (
                  <li key={`${keyPrefix}-near_sl-${item.data.id}`}>
                    <FeedItemRow
                      className="border-orange-200 bg-orange-50/50"
                      onClick={() => handleNavigateToDetail(item.data)}
                      icon={iconForType('near_sl')}
                      action={
                        <FeedActionLink
                          label={t('trading:dashboard.feedActionEditSl')}
                          tone="orange"
                          onClick={openFeedQuickAction(item.data, 'sltp')}
                        />
                      }
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {t('trading:dashboard.feedNearStopLossTitle', { symbol: item.data.symbol })}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {t('trading:dashboard.feedNearStopLossBody', {
                          distance: distance.toFixed(2),
                          stopLoss:
                            item.data.stop_loss != null
                              ? Number(item.data.stop_loss).toFixed(2)
                              : '-',
                          currentPrice:
                            item.data.current_price != null
                              ? Number(item.data.current_price).toFixed(2)
                              : '-'
                        })}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {item.data.entry_date
                          ? new Date(item.data.entry_date).toLocaleDateString(undefined, {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : ''}
                      </p>
                    </FeedItemRow>
                  </li>
                )
              }
              if (item.kind === 'high_risk_exposure') {
                const criteriaSuffix = item.data.criteria
                  ? t('trading:dashboard.feedHighRiskExposureCriteriaSuffix', { criteria: item.data.criteria })
                  : ''
                return (
                  <li key={`${keyPrefix}-high_risk_exposure-${item.data.widgetId}`}>
                    <FeedItemRow
                      className="border-red-200 bg-red-50/50"
                      icon={iconForType('high_risk_exposure')}
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {t('trading:dashboard.feedHighRiskExposureTitle')}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {t('trading:dashboard.feedHighRiskExposureBody', {
                          riskPercent: item.data.riskPercent.toFixed(2).replace('.', ','),
                          threshold: HIGH_RISK_EXPOSURE_THRESHOLD,
                          criteriaSuffix,
                        })}
                      </p>
                    </FeedItemRow>
                  </li>
                )
              }
              if (item.kind === 'add_first_strategy') {
                return (
                  <li key={`${keyPrefix}-add_first_strategy`}>
                    <FeedItemRow
                      className="border-purple-200 bg-purple-50/50"
                      onClick={() => navigate('/app/create-strategy')}
                      icon={iconForType('add_first_strategy')}
                      action={
                        <FeedActionLink
                          label={t('trading:dashboard.feedActionCreateStrategy')}
                          tone="purple"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate('/app/create-strategy')
                          }}
                        />
                      }
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {t('trading:dashboard.feedAddFirstStrategyTitle')}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {t('trading:dashboard.feedAddFirstStrategyBody')}
                      </p>
                    </FeedItemRow>
                  </li>
                )
              }
              if (item.kind === 'after_exit_review') {
                return (
                  <li key={`${keyPrefix}-after_review-${item.data.id}`}>
                    <FeedItemRow
                      className="gap-3 p-2 border-blue-200 bg-blue-50/50"
                      onClick={() => handleNavigateToDetail(item.data)}
                      icon={iconForType('after_exit_review')}
                      action={
                        <FeedActionLink
                          label={t('trading:dashboard.feedActionAddReview')}
                          tone="blue"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleNavigateToDetailWithReview(item.data)
                          }}
                        />
                      }
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {t('trading:dashboard.feedAfterExitReviewTitle', { symbol: item.data.symbol })}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {t('trading:dashboard.feedAfterExitReviewBody')}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {item.data.exit_date
                          ? new Date(item.data.exit_date).toLocaleDateString(undefined, {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : ''}
                      </p>
                    </FeedItemRow>
                  </li>
                )
              }
              if (item.kind === 'sl_reached' || item.kind === 'tp_reached') {
                const isSL = item.kind === 'sl_reached'
                const titleKey = isSL
                  ? 'trading:dashboard.feedStopLossReachedTitle'
                  : 'trading:dashboard.feedTakeProfitReachedTitle'
                const bodyKey = isSL
                  ? 'trading:dashboard.feedStopLossReachedBody'
                  : 'trading:dashboard.feedTakeProfitReachedBody'
                return (
                  <li key={`${keyPrefix}-${item.kind}-${item.data.id}`}>
                    <FeedItemRow
                      className={isSL ? 'border-red-200 bg-red-50/50' : 'border-green-200 bg-green-50/50'}
                      onClick={() => handleNavigateToDetail(item.data)}
                      icon={iconForType(isSL ? 'stop_loss' : 'take_profit')}
                      action={
                        <FeedActionLink
                          label={
                            isSL
                              ? t('trading:tradeList.quickActions.fullExit')
                              : t('trading:tradeList.quickActions.partialClose')
                          }
                          tone={isSL ? 'red' : 'green'}
                          onClick={openFeedQuickAction(item.data, isSL ? 'fullExit' : 'partialClose')}
                        />
                      }
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {t(titleKey, { symbol: item.data.symbol })}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {t(bodyKey, {
                          stopLoss:
                            item.data.stop_loss != null
                              ? Number(item.data.stop_loss).toFixed(2)
                              : '-',
                          takeProfit:
                            item.data.take_profit != null
                              ? Number(item.data.take_profit).toFixed(2)
                              : '-',
                          currentPrice:
                            item.data.current_price != null
                              ? Number(item.data.current_price).toFixed(2)
                              : '-'
                        })}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {item.data.entry_date
                          ? new Date(item.data.entry_date).toLocaleDateString(undefined, {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : ''}
                      </p>
                    </FeedItemRow>
                  </li>
                )
              }
              return (
                <li key={`${keyPrefix}-missing-${item.data.id}`}>
                  <FeedItemRow
                    className="border-amber-200 bg-amber-50/50"
                    onClick={() => handleNavigateToDetail(item.data)}
                    icon={iconForType('missing_data')}
                    action={
                      <FeedActionLink
                        label={t('trading:tradeList.quickActions.fullEdit')}
                        tone="amber"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEditTrade(item.data)
                        }}
                      />
                    }
                  >
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {t('trading:dashboard.feedMissingDataTitle', { symbol: item.data.symbol })}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {t(getMissingDataBodyKey(item.data))}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-1">
                      {item.data.entry_date
                        ? new Date(item.data.entry_date).toLocaleDateString(undefined, {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : ''}
                    </p>
                  </FeedItemRow>
                </li>
              )
            })}
          </ul>
        )

  const scrollArea = (
    <div
      className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
      {...scrollContainerProps}
      onWheel={handleScrollGuard}
    >
      {renderFeedListContent('desktop')}
    </div>
  )

  if (presentation === 'count') return null

  if (presentation === 'studio') {
    return (
      <>
        {feedQuickAction && (
          <TradeListQuickActions
            trade={resolveTradeForQuickAction(feedQuickAction.trade)}
            directAction={feedQuickAction.kind}
            onDirectClose={() => setFeedQuickAction(null)}
            onUpdated={handleQuickActionUpdated}
            onEditTrade={onStudioEditTrade ? (trade) => onStudioEditTrade(trade as OpenTrade) : undefined}
          />
        )}
        {studioSelectedMessage && (
          <InboxStudioModal message={studioSelectedMessage} onClose={() => setStudioSelectedKey(null)} />
        )}
        <div className="at-inbox-studio-list">
          {loading ? (
            <p className="at-inbox-studio-empty">{t('common:header.loading')}</p>
          ) : inboxVisibleFeedItems.length === 0 ? (
            <p className="at-inbox-studio-empty">{t('trading:dashboard.feedEmpty')}</p>
          ) : (
            <ul className="at-inbox-mail-list">
              {inboxVisibleFeedItems.map((item) => {
                const message = buildInboxStudioMessage(item as InboxFeedItem, t, navigate, {
                  openTrade: (trade) => handleNavigateToDetail(resolveOpenTrade(trade)),
                  openTradeById: (tradeId) => {
                    const trade = trades.find((entry) => entry.id === tradeId)
                    if (trade) handleNavigateToDetail(trade as OpenTrade)
                  },
                  openTradeReview: (trade) => handleNavigateToDetailWithReview(resolveOpenTrade(trade)),
                  editTrade: (trade) => handleEditTrade(resolveOpenTrade(trade)),
                  quickAction: (trade, kind) => setFeedQuickAction({ trade: resolveOpenTrade(trade), kind }),
                })
                const toneRow =
                  message.tone === 'danger'
                    ? 'is-danger'
                    : message.tone === 'warning'
                      ? 'is-warning'
                      : message.tone === 'success'
                        ? 'is-success'
                        : message.tone === 'info'
                          ? 'is-info'
                          : message.tone === 'purple'
                            ? 'is-purple'
                            : ''
                const itemKey = feedItemKey(item as InboxFeedItem)
                return (
                  <li key={itemKey} className="at-inbox-mail-item">
                    <button
                      type="button"
                      className={`at-inbox-mail-row ${message.unread ? 'is-unread' : ''} ${toneRow}`}
                      onClick={() => openStudioMessage(item)}
                    >
                      <span className="at-inbox-mail-avatar" aria-hidden>T</span>
                      <span className="at-inbox-mail-main">
                        <span className="at-inbox-mail-top">
                          <strong className="at-inbox-mail-subject">{message.subject}</strong>
                          <time>
                            {message.date
                              ? message.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                              : '—'}
                          </time>
                        </span>
                        <span className="at-inbox-mail-preview">{message.preview}</span>
                        <span className="at-inbox-mail-category">{message.category}</span>
                      </span>
                      {message.unread && <span className="at-inbox-mail-unread" aria-label="Unread" />}
                    </button>
                    <button
                      type="button"
                      className="at-inbox-mail-dismiss"
                      aria-label={t('trading:inboxStudio.dismiss')}
                      title={t('trading:inboxStudio.dismiss')}
                      onClick={() => dismissFeedItem(item)}
                    >
                      ×
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </>
    )
  }

  if (presentation === 'page') {
    return (
      <>
        {feedQuickAction && (
          <TradeListQuickActions
            trade={resolveTradeForQuickAction(feedQuickAction.trade)}
            directAction={feedQuickAction.kind}
            onDirectClose={() => setFeedQuickAction(null)}
            onUpdated={handleQuickActionUpdated}
          />
        )}
        <div className="at-inbox-list">{renderFeedListContent('desktop')}</div>
      </>
    )
  }

  return (
    <>
      {feedQuickAction && (
        <TradeListQuickActions
          trade={resolveTradeForQuickAction(feedQuickAction.trade)}
          directAction={feedQuickAction.kind}
          onDirectClose={() => setFeedQuickAction(null)}
          onUpdated={handleQuickActionUpdated}
        />
      )}

      {/* Mobil: Slider altında tek satır, tıklayınca aşağı dropdown */}
      <section
        aria-label={t('trading:dashboard.feed')}
        className={`md:hidden ${cardClass} overflow-hidden`}
      >
        <button
          type="button"
          onClick={() => setMobileExpanded(!mobileExpanded)}
          className="w-full flex items-center justify-between gap-2 py-2 text-left"
          aria-expanded={mobileExpanded}
        >
          <h2 className="text-base font-semibold text-gray-900">
            <DashboardWidgetTitle
              widgetId="feed"
              labelKey="trading:dashboard.feed"
              className="font-semibold normal-case text-base text-gray-900"
            />
          </h2>
          <span className="flex items-center gap-2 text-sm text-gray-500">
            {feedItems.length > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 text-xs font-medium">
                {feedItems.length}
              </span>
            )}
            <svg
              className={`w-5 h-5 transition-transform ${mobileExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </button>
        {mobileExpanded && (
          <div
            className="border-t border-gray-200 mt-2 pt-2 max-h-[min(60vh,320px)] overflow-y-auto"
            {...scrollContainerProps}
          >
            {renderFeedListContent('mobile')}
          </div>
        )}
      </section>

      {/* Desktop: Tam yükseklik, her zaman açık */}
      <section
        aria-label={t('trading:dashboard.feed')}
        className={`hidden md:flex ${cardClass} flex-col min-h-0 h-full overflow-hidden`}
      >
        <h2 className="text-base md:text-lg font-semibold mb-3 text-gray-900 flex-shrink-0">
          <DashboardWidgetTitle
            widgetId="feed"
            labelKey="trading:dashboard.feed"
            className="font-semibold normal-case text-base md:text-lg text-gray-900"
          />
        </h2>
        {scrollArea}
      </section>
    </>
  )
}
